import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Breadcrumbs,
  Button,
  Group,
  Stack,
  Alert,
  ThemeIcon,
  Text,
  LoadingOverlay,
  Modal,
  Center,
  Avatar,
  ActionIcon,
  Table,
  FileButton,
  Loader,
  useMantineTheme,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconFile,
  IconChevronRight,
  IconHome,
  IconFolderFilled,
  IconUpload,
  IconExternalLink,
  IconFileUpload,
  IconDownload,
} from '@tabler/icons-react';
import { FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { useGraphService } from '@repo/services';
import {
  DriveItem,
  isVideoFile,
  isImageFile,
  uploadFilesToFolder,
  moveDriveItem as moveItemUtil,
  notifyMove,
  notifyMoveFailed,
  // Office helpers
  isOfficeFile,
  openOfficeInDesktop,
  // Download helpers
  downloadDriveItem,
} from './driveUtils';
import { DriveMediaBrowser } from './DriveMediaBrowser';
import { RenameDriveFileButton } from './RenameDriveFileButton';

interface DriveExplorerProps {
  driveId: string;
  rootFolderId:  string | undefined ;
  /** Delegate image/video viewing to a shared viewer when provided. */
  onMediaItemClick?: (item: DriveItem, folderId: string) => void;
  /** Reports the hovered file/folder row; null clears it on leave, navigation, or unmount. */
  onItemHover?: (item: DriveItem | null) => void;
  /** Enables file renaming and lets the owning app permission-gate the control. */
  renderRenameControl?: (control: React.ReactNode) => React.ReactNode;
  onFileRenamed?: () => void;
  /** Called after a folder listing loads successfully, including folder navigation. */
  onFolderRefreshed?: (folderId: string | null) => void;
}

const rootFolder = '';
const INTERNAL_DRAG_TYPE = 'application/x-drive-item-id';

const UPLOAD_MIME_TYPES = [
  MIME_TYPES.pdf,
  MIME_TYPES.ppt,
  MIME_TYPES.pptx,
  MIME_TYPES.docx,
  MIME_TYPES.xls,
  MIME_TYPES.xlsx,
  MIME_TYPES.png,
  MIME_TYPES.doc,
  MIME_TYPES.heic,
  MIME_TYPES.jpeg,
  MIME_TYPES.mp4,
  'video/quicktime',
  'video/mpeg',
  'audio/mpeg',
  '.kmz',
  'application/vnd.google-earth.kmz',
  '.kml',
  'application/vnd.google-earth.kml+xml',
  '.shp',
  '.shx',
  '.dbf',
  '.xml',
  'application/xml',
  'text/xml',
  'application/zip',
];
const UPLOAD_INPUT_ACCEPT = UPLOAD_MIME_TYPES.join(',');

// Build quick lookup sets: some entries in UPLOAD_MIME_TYPES are actual mime types (contain '/'),
// others are file extensions (start with '.') — use both to validate dropped File objects.
const ALLOWED_MIME_TYPES = new Set(UPLOAD_MIME_TYPES.filter((s) => s.includes('/')));
const ALLOWED_EXTENSIONS = new Set(UPLOAD_MIME_TYPES.filter((s) => s.startsWith('.')).map((s) => s.toLowerCase()));
const BLACKLIST_FILENAMES = new Set(['.DS_Store', 'Thumbs.db']);

const isAllowedFile = (file: File) => {
  if (!file || !file.name) return false;
  const name = file.name;
  if (BLACKLIST_FILENAMES.has(name)) return false;
  const lower = name.toLowerCase();

  // If mime type is present and allowed
  if (file.type && ALLOWED_MIME_TYPES.has(file.type)) return true;

  // Check extension from name in a type-safe way
  const m = lower.match(/(\.[^./\\]+)$/);
  const ext = m && m[1] ? m[1] : '';
  if (ext && ALLOWED_EXTENSIONS.has(ext)) return true;

  return false;
};

/**
 * A Microsoft Graph Drive file browser that renders a folder tree with breadcrumb navigation, drag-and-drop file upload, drag-and-drop item reordering, download buttons, and a media browser for images/videos.
 *
 * Requires a driveId and rootFolderId prop. Uses Graph API to list children, upload files, and move items. Renders a progress modal during uploads.
 */
export const DriveExplorer: React.FC<DriveExplorerProps> = ({ driveId, rootFolderId, onMediaItemClick, onItemHover, renderRenameControl, onFileRenamed, onFolderRefreshed }) => {
  const { graphClient } = useGraphService();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [items, setItems] = useState<DriveItem[]>([]);
  const [pathStack, setPathStack] = useState<{ id: string; name: string }[]>([]);
  const hoveredItemRef = useRef<DriveItem | null>(null);
  const hoverCallbackRef = useRef(onItemHover);
  useEffect(() => {
    hoverCallbackRef.current = onItemHover;
  }, [onItemHover]);
  const currentHoverFolderId = pathStack[pathStack.length - 1]?.id ?? rootFolderId;
  useEffect(() => {
    return () => {
      if (hoveredItemRef.current) {
        hoveredItemRef.current = null;
        hoverCallbackRef.current?.(null);
      }
    };
  }, [driveId, currentHoverFolderId, items]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgressMap, setUploadProgressMap] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, string>>({});

  const [isDragging, setIsDragging] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);
  const [mediaStartId, setMediaStartId] = useState<string | null>(null);
  // track which file name is actively uploading (so only that file shows a spinner)
  const [currentUploading, setCurrentUploading] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pointerPos, setPointerPos] = useState<{ x: number; y: number } | null>(null);
  const folderRefreshedCallbackRef = useRef(onFolderRefreshed);
  useEffect(() => {
    folderRefreshedCallbackRef.current = onFolderRefreshed;
  }, [onFolderRefreshed]);

  const formatBytes = (bytes?: number | null) => {
    if (bytes === undefined || bytes === null) return '—';
    if (bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getCurrentFolderId = () => pathStack[pathStack.length - 1]?.id ?? rootFolderId;

  const fetchItems = useCallback(async (folderId?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const response = folderId
        ? await graphClient.api(`/drives/${driveId}/items/${folderId}/children`).expand('listItem,thumbnails').get()
        : await graphClient.api(`/drives/${driveId}/root:/${rootFolder}:/children`).expand('listItem,thumbnails').get();
      setItems(response.value);
      folderRefreshedCallbackRef.current?.(folderId ?? null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch items');
    } finally {
      setLoading(false);
    }
  }, [graphClient, driveId]);

  // Read DataTransferItem entries recursively (webkitGetAsEntry / getAsEntry)
  const readDroppedEntries = async (items: DataTransferItemList | FileWithPath[] | null) : Promise<{ file: File; relativePath: string }[]> => {
    // If we get FileWithPath[] from Dropzone, use webkitRelativePath or name
    if (!items) return [];
    // If items is an array of FileWithPath (from Dropzone), convert:
    if (Array.isArray(items)) {
      return (items as FileWithPath[]).map(f => ({
        file: f as unknown as File,
        relativePath: (f as any).webkitRelativePath || (f as any).path || f.name,
      }));
    }

    // Otherwise traverse DataTransferItemList
    const dtItems = items as DataTransferItemList;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entries: any[] = [];
    // convert to array for consistent iteration across typings
    const dtArray = Array.from(dtItems as unknown as DataTransferItem[]);
    dtArray.forEach((it) => {
      if (!it) return;
      // webkitGetAsEntry may not exist on all browsers; fall back to getAsFile
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entry = (it as any).webkitGetAsEntry ? (it as any).webkitGetAsEntry() : null;
      if (entry) entries.push(entry);
      else {
        const file = it.getAsFile();
        if (file) entries.push({ isFile: true, name: file.name, file });
      }
    });

    const results: { file: File; relativePath: string }[] = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const traverse = (entry: any, path = ''): Promise<void> => {
      return new Promise((resolve) => {
        if (!entry) return resolve();
        if (entry.isFile) {
          entry.file((file: File) => {
            results.push({ file, relativePath: path + entry.name });
            resolve();
          }, () => resolve());
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const readBatch = () => {
            reader.readEntries(async (entriesBatch: any[]) => {
              if (!entriesBatch || entriesBatch.length === 0) return resolve();
              await Promise.all(entriesBatch.map((en) => traverse(en, path + entry.name + '/')));
              // read next batch (the API returns batches)
              readBatch();
            }, () => resolve());
          };
          readBatch();
        } else {
          resolve();
        }
      });
    };

    await Promise.all(entries.map((e) => traverse(e)));
    return results;
  };

  // Ensure the folder chain exists under parentId, returning final folder id
  const ensureFolderChain = async (parentId: string | undefined | null, relativeDir: string) : Promise<string | undefined> => {
    if (!relativeDir || relativeDir === '') return parentId ?? undefined;
    let currentParentId: string | undefined = parentId ?? rootFolderId;
    const segments = relativeDir.split('/').filter(s => s);
    for (const seg of segments) {
      // list children and find folder with name === seg
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const listResp: any = await graphClient.api(`/drives/${driveId}/items/${currentParentId}/children`).top(999).get();
        const existing = (listResp.value || []).find((v: any) => v.name === seg && v.folder);
        if (existing) {
          currentParentId = existing.id;
        } else {
          const created = await graphClient.api(`/drives/${driveId}/items/${currentParentId}/children`).post({
            name: seg,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename"
          });
          currentParentId = created.id;
        }
      } catch (err: any) {
        setError(err?.message || `Failed to create folder ${seg}`);
        return undefined;
      }
    }
    return currentParentId;
  };

  // Process dropped files (file objects with relativePath) and upload them creating folder structure
  const processDroppedFiles = async (filesWithPaths: { file: File; relativePath: string }[], destParentFolderId?: string | null) => {
    if (!filesWithPaths || filesWithPaths.length === 0) return;
    // Filter out not-allowed files (like .DS_Store) before starting
    const allowedFiles = filesWithPaths.filter(fwp => isAllowedFile(fwp.file));
    const rejectedFiles = filesWithPaths.filter(fwp => !isAllowedFile(fwp.file));

    // Mark rejected files in status so user sees they were ignored
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(({ relativePath }) => {
        const baseName = relativePath.split('/').pop() || relativePath;
        setUploadStatus(prev => ({ ...prev, [baseName]: '❌ Rejected (not allowed)' }));
      });
    }

    if (allowedFiles.length === 0) {
      // nothing to upload
      setError(rejectedFiles.length > 0 ? 'No allowed files to upload' : null);
      // ensure progress modal shows rejections if any
      if (rejectedFiles.length > 0) setShowProgressModal(true);
      return;
    }

    setShowProgressModal(true);
    setError(null);

    // Group files by their relative folder path
    const grouped: Record<string, { file: File; name: string }[]> = {};
    for (const { file, relativePath } of allowedFiles) {
      const normalized = relativePath.replace(/^\/+/, '');
      const parts = normalized.split('/');
      const name = parts.pop() || file.name;
      const dir = parts.join('/');
      const bucket = grouped[dir] ?? (grouped[dir] = []);
      bucket.push({ file, name });
      // initialize status/progress
      const idKey = `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setUploadProgressMap(prev => ({ ...prev, [idKey]: 0 }));
      setUploadStatus(prev => ({ ...prev, [name]: 'Uploading...' }));
    }

    // For each folder group, ensure folder chain and upload files into it
    for (const [dir, list] of Object.entries(grouped)) {
      const targetFolderId = await ensureFolderChain(destParentFolderId || getCurrentFolderId(), dir);
      if (!targetFolderId) {
        // mark failures
        list.forEach(f => setUploadStatus(prev => ({ ...prev, [f.name]: '❌ Failed to create target folder' })));
        continue;
      }

      // upload files to this folder (sequentially to preserve progress UI)
      for (const { file, name } of list) {
        // create unique progress key per file name (preserve earlier created key search)
        const fileKeyPrefix = `${name}-`;
        const localId = `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setUploadProgressMap(prev => ({ ...prev, [localId]: 0 }));
        setUploadStatus(prev => ({ ...prev, [name]: 'Uploading...' }));
        setCurrentUploading(name);
        try {
          await uploadFilesToFolder(graphClient, driveId, targetFolderId, [file], {}, {
            onProgress: (f, percent) => {
              const key = Object.keys(uploadProgressMap).find(k => k.startsWith(fileKeyPrefix)) || localId;
              setUploadProgressMap(prev => ({ ...prev, [key]: Math.round(percent) }));
            },
            onStatus: (f, status) => {
              setUploadStatus(prev => ({ ...prev, [name]: status }));
            },
            afterAll: async () => {
              // refresh listing for current folder
              const cf = getCurrentFolderId();
              if (cf) await fetchItems(cf);
            }
          });
        } catch (err: any) {
          setUploadStatus(prev => ({ ...prev, [name]: `❌ ${err?.message || 'Upload failed'}` }));
        } finally {
          // clear active uploading marker for this file once finished (success or failure)
          setCurrentUploading((prev) => (prev === name ? null : prev));
        }
      }
    }
  };

  const handleDropMultiple = async (files: FileWithPath[]) => {
    // Keep existing file-only path behavior (Dropzone supplies webkitRelativePath for folder drops sometimes)
    const mapped = (files || []).map((f) => ({
      file: f as unknown as File,
      relativePath: (f as any).webkitRelativePath || (f as any).path || f.name,
    }));
    await processDroppedFiles(mapped);
  };

  const isExternalFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const containerCursor: React.CSSProperties['cursor'] = draggingItemId ? 'grabbing' : (isDragging ? 'copy' : 'default');

  const nameTextStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    width: '100%',
  };

  // Handle native drop on the container (allow folder structure)
  const handleContainerNativeDrop = async (e: React.DragEvent, destFolderId?: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    try {
      const items = e.dataTransfer?.items || null;
      if (items) {
        const filesWithPaths = await readDroppedEntries(items);
        if (filesWithPaths.length === 0) {
          // Fallback to files list
          const filesArr: { file: File; relativePath: string }[] = [];
          for (let i = 0; i < (e.dataTransfer?.files?.length || 0); i++) {
            const f = e.dataTransfer!.files.item(i)!;
            filesArr.push({ file: f, relativePath: (f as any).webkitRelativePath || f.name });
          }
          await processDroppedFiles(filesArr, destFolderId);
        } else {
          await processDroppedFiles(filesWithPaths, destFolderId);
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to process dropped files');
    }
  };

  // Move a drive item into another folder (internal)
  const moveDriveItem = async (itemId: string, destFolderId: string) => {
    if (!destFolderId) return;
    setLoading(true);
    setError(null);
    const srcName = items.find((i) => i.id === itemId)?.name || 'Item';
    const destName = items.find((i) => i.id === destFolderId)?.name || 'folder';
    try {
      await moveItemUtil(graphClient, driveId, itemId, destFolderId);
      const currentFolderId = getCurrentFolderId();
      if (currentFolderId) await fetchItems(currentFolderId);
      notifyMove(srcName, destName);
    } catch (err: any) {
      setError(err.message || 'Failed to move item');
      notifyMoveFailed(err.message || 'Unable to move item');
    } finally {
      setLoading(false);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    const newStack = pathStack.slice(0, index + 1);
    setPathStack(newStack);
    const folderId = newStack[newStack.length - 1]?.id;
    fetchItems(folderId);
  };

  const handleFileClick = async (item: DriveItem) => {
    if (item.folder) {
      setPathStack((prev) => [...prev, { id: item.id, name: item.name }]);
      await fetchItems(item.id);
    } else if (isImageFile(item) || isVideoFile(item)) {
      const folderId = getCurrentFolderId();
      if (onMediaItemClick && folderId) {
        onMediaItemClick(item, folderId);
      } else {
        setMediaStartId(item.id);
        setShowMediaBrowser(true);
      }
    } else {
      const fileUrl = item.webUrl;
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      }
    }
  };

  useEffect(() => {
    setPathStack([]);
    if (graphClient && rootFolderId) {
      setItems([])
      fetchItems(rootFolderId);
    }
  }, [graphClient, driveId, rootFolderId, fetchItems]);

  useEffect(() => {
    if (showProgressModal) {
      const allDone = Object.values(uploadStatus).every(
        (status) => status.startsWith('✅') || status.startsWith('❌')
      );
      if (allDone && Object.keys(uploadStatus).length > 0) {
        setTimeout(() => {
          setShowProgressModal(false);
          setUploadProgressMap({});
          setUploadStatus({});
        }, 1500);
      }
    }
  }, [uploadStatus, showProgressModal]);


  if (!rootFolderId) {
    return <Center mih={100}>Not Found</Center>
  }

  return (
    <Stack
      ref={containerRef}
      mih={200}
      p={5}
      style={{ position: 'relative', cursor: containerCursor }}
      onDragEnter={(e) => { if (isExternalFileDrag(e)) setIsDragging(true); }}
      onDragOver={(e) => {
        if (draggingItemId && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setPointerPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }
        if (isExternalFileDrag(e)) {
          e.preventDefault(); // allow drop
          e.dataTransfer.dropEffect = 'copy';
        }
      }}
      onDragLeave={(e) => {
        // clear isDragging when the pointer leaves the container bounds
        try {
          const rect = containerRef.current?.getBoundingClientRect();
          if (!rect) { setIsDragging(false); return; }
          const x = e.clientX;
          const y = e.clientY;
          if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setIsDragging(false);
            setDragOverFolderId(null);
          }
        } catch {
          setIsDragging(false);
        }
      }}
      onDrop={(e) => handleContainerNativeDrop(e)}
    >
      <LoadingOverlay visible={loading} />
      {error && (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      )}
      <Group  justify="space-between" >
      <Breadcrumbs separatorMargin={0} separator={<IconChevronRight size={16} />}>

        {rootFolderId && (<Button
          variant="subtle"
          size={'compact-sm'}
          onClick={() => {
            setPathStack([]);
            fetchItems(rootFolderId);
          }}
          leftSection={<IconHome size={16} />}
        >
          Root
        </Button>)}
        {pathStack.map((folder, index) => (
          <Button size={'compact-sm'}
            key={folder.id}
            variant="subtle"
            onClick={() => handleBreadcrumbClick(index)}
          >
            {folder.name}
          </Button>
        ))}
      </Breadcrumbs>
        <FileButton
          accept={UPLOAD_INPUT_ACCEPT}
          multiple
          onChange={(files) => {
            if (!files) return;
            const fileList = Array.isArray(files) ? files : [files];
            handleDropMultiple(fileList as unknown as FileWithPath[]);
          }}
        >
          {(props) => (
            <Button  {...props} leftSection={<IconFileUpload />} size={'compact-sm'} variant="subtle">
              Upload Files
            </Button>
          )}
        </FileButton>

      </Group>
      {items.length > 0 ? (
        <Table verticalSpacing="xs" highlightOnHover withRowBorders={false}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ textAlign: 'left' }}>Name</Table.Th>
              <Table.Th style={{ textAlign: 'right', width: 80 }} />
              <Table.Th style={{ textAlign: 'right', width: 120 }}>File Size</Table.Th>
              <Table.Th style={{ textAlign: 'right', width: 180 }}>Modified</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => {
              const isFolder = !!item.folder;
              const isDragTarget = dragOverFolderId === item.id;
              const isDraggingThis = draggingItemId === item.id;
              const isInternalDragging = !!draggingItemId;
              const showOfficeIcon = !isFolder && isOfficeFile(item);

              const modifiedRaw = (item as any).lastModifiedDateTime;
              const modified = modifiedRaw ? new Date(modifiedRaw).toLocaleString() : '—';
              const modifiedBy = (item as any).lastModifiedBy?.user?.displayName
                || (item as any).lastModifiedBy?.application?.displayName
                || '—';

              return (
                <Table.Tr
                  key={item.id}
                  onPointerEnter={(event) => {
                    if (event.pointerType === 'touch') return;
                    hoveredItemRef.current = item;
                    onItemHover?.(item);
                  }}
                  onPointerLeave={() => {
                    if (hoveredItemRef.current?.id !== item.id) return;
                    hoveredItemRef.current = null;
                    onItemHover?.(null);
                  }}
                  draggable={!isFolder}
                  onDragStart={(e) => {
                    if (!isFolder) {
                      setDraggingItemId(item.id);
                      e.dataTransfer.setData(INTERNAL_DRAG_TYPE, item.id);
                      e.dataTransfer.effectAllowed = 'move';
                      try { e.dataTransfer.setDragImage(e.currentTarget as HTMLElement, 16, 16); }
                      catch (err) { console.error(err); }
                      (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
                      setIsDragging(false);
                    }
                  }}
                  onDragEnd={(e) => {
                    setDragOverFolderId(null);
                    setDraggingItemId(null);
                    setPointerPos(null);
                    (e.currentTarget as HTMLElement).style.cursor = '';
                  }}
                  onDragOver={(e) => {
                    const hasInternal = Array.from(e.dataTransfer?.types || []).includes(INTERNAL_DRAG_TYPE);
                    const hasExternalFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
                    if (hasInternal) {
                      if (isFolder) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverFolderId(item.id);
                      } else {
                        e.dataTransfer.dropEffect = 'none';
                      }
                    } else if (hasExternalFiles && isFolder) {
                      // allow external files/folders to be dropped on folder row
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                      setDragOverFolderId(item.id);
                    }
                  }}
                  onDragEnter={(e) => {
                    const hasInternal = Array.from(e.dataTransfer?.types || []).includes(INTERNAL_DRAG_TYPE);
                    const hasExternalFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
                    if (hasInternal && isFolder) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverFolderId(item.id);
                    } else if (hasExternalFiles && isFolder) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                      setDragOverFolderId(item.id);
                    }
                  }}
                  onDragLeave={(e) => {
                    const related = e.relatedTarget as HTMLElement | null;
                    if (!related || !related.closest(`[data-row-id="${item.id}"]`)) {
                      setDragOverFolderId((prev) => (prev === item.id ? null : prev));
                    }
                  }}
                  onDrop={async (e) => {
                    // Internal move
                    const draggedItemId = e.dataTransfer.getData(INTERNAL_DRAG_TYPE);
                    // If external file(s)/folders dropped onto a folder row, process them into this folder
                    const hasExternalFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
                    setDragOverFolderId(null);
                    setDraggingItemId(null);
                    setPointerPos(null);
                    if (hasExternalFiles && isFolder) {
                      e.preventDefault();
                      await handleContainerNativeDrop(e, item.id);
                      return;
                    }
                    if (!draggedItemId) return;
                    if (!isFolder) return;
                    if (draggedItemId === item.id) return;
                    e.preventDefault();
                    moveDriveItem(draggedItemId, item.id);
                  }}
                  data-row-id={item.id}
                  style={{
                    backgroundColor: isFolder && isDragTarget
                      ? (isDark ? theme.colors.blue[9] : theme.colors.blue[0])
                      : (isDraggingThis ? (isDark ? theme.colors.dark[5] : theme.colors.gray[1]) : 'transparent'),
                    outline: isFolder && isDragTarget
                      ? `2px dashed ${theme.colors.blue[5]}`
                      : (isDraggingThis ? `1px solid ${isDark ? theme.colors.dark[3] : theme.colors.gray[4]}` : 'none'),
                    outlineOffset: isFolder && isDragTarget ? '2px' : undefined,
                    borderRadius: 6,
                    padding: isFolder && isDragTarget ? 4 : undefined,
                    opacity: isDraggingThis ? 0.7 : 1,
                    cursor: isFolder
                      ? (isInternalDragging ? 'pointer' : 'default')
                      : (isDraggingThis ? 'grabbing' : (isInternalDragging ? 'not-allowed' : 'grab')),
                  }}
                  title={!isFolder && isInternalDragging ? 'You can only drop into folders' : undefined}
                >
                  <Table.Td style={{maxWidth:'100%'}}>
                    <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                      {isFolder ? (
                        <ThemeIcon variant="transparent" color="#daa520">
                          <IconFolderFilled size={36} />
                        </ThemeIcon>
                      ) : (
                        <Avatar
                          src={item.thumbnails?.[0]?.medium?.url || item.thumbnails?.[0]?.small?.url}
                          size={36}
                          radius="sm"
                          variant="filled"
                          color="gray"
                          alt={item.name}
                        >
                          <IconFile size={20} />
                        </Avatar>
                      )}
                      <Group gap={4} wrap='nowrap' data-file-name style={{ minWidth: 0 }}>
                      <Button
                        variant="transparent"
                        onClick={() => handleFileClick(item)}
                        style={{ fontWeight: 'normal' }}
                        w={'fit-content'}
                        ta={'left'}
                        p={0}
                        pl={5}
                      >
                        <div style={nameTextStyle}>
                          {item.name}
                        </div>
                      </Button>
                      {!isFolder && renderRenameControl?.(
                        <RenameDriveFileButton driveId={driveId} item={item} onRenamed={(id, name) => {
                          setItems((previous) => previous.map((file) => file.id === id ? { ...file, name } : file));
                          onFileRenamed?.();
                        }} />
                      )}
                      </Group>
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      {!isFolder && (
                        <ActionIcon
                          variant="subtle"
                          aria-label="Download file"
                          title="Download file"
                          onClick={(e) => { e.stopPropagation(); downloadDriveItem(graphClient, driveId, item); }}
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                      )}
                      {showOfficeIcon && (
                        <ActionIcon
                          variant="subtle"
                          aria-label="Open in desktop Office app"
                          title="Open in desktop Office app"
                          onClick={(e) => { e.stopPropagation(); openOfficeInDesktop(item); }}
                        >
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Text  size={'xs'}>{isFolder
                      ? (typeof (item.folder as any)?.childCount === 'number'
                          ? `${(item.folder as any).childCount} item${(item.folder as any).childCount === 1 ? '' : 's'}`
                          : '—')
                      : formatBytes((item as any).size)}
                    </Text>
                  </Table.Td>

                  <Table.Td p={0} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><Stack>
                    <Text size={'xs'}>{modified}</Text></Stack>
                    <Text size={'xs'}>{modifiedBy}</Text>
                  </Table.Td>

                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      ) : (
        !loading && (
          <Text c="dimmed" ta="center">
            This folder is empty. Drag and drop files here to upload.
          </Text>
        )
      )}
      {isDragging && rootFolderId && (
        // translucent non-blocking overlay: pointerEvents none so folder rows still receive drag events
        <div
          style={{
            position: 'absolute',
            zIndex: 9999,
            left: 0,
            top: 50,
            width: '100%',
            height: 'calc(100% - 50px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // mostly transparent background so the file list remains visible underneath
            background: dragOverFolderId
              ? 'rgba(255,255,255,0.02)'
              : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
            // allow underlying rows to receive drag events so they can act as drop targets
            pointerEvents: 'none',
          }}
        >
          {!dragOverFolderId && (
            <Group justify="center" gap="xl" style={{ pointerEvents: 'none' }}>
              <Center>
                <IconUpload size={28} />
                <Text size="sm">Drop files here to upload</Text>
              </Center>
            </Group>
          )}
        </div>
      )}
       {draggingItemId && dragOverFolderId && pointerPos && (
         <div
           style={{
             position: 'absolute',
             left: pointerPos.x + 12,
             top: pointerPos.y - 12,
             background: 'rgba(34, 139, 230, 0.95)',
             color: 'white',
             padding: '4px 8px',
             borderRadius: 6,
             fontSize: 12,
             boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
             pointerEvents: 'none',
             zIndex: 10000,
             whiteSpace: 'nowrap',
           }}
         >
           {`Move to ${items.find((i) => i.id === dragOverFolderId)?.name ?? 'folder'}`}
         </div>
       )}

      <Modal
        opened={showProgressModal}
        onClose={() => {
          setShowProgressModal(false);
          setUploadProgressMap({});
          setUploadStatus({});
        }}
        title="Upload Progress"
        centered
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        {Object.entries(uploadStatus).map(([fileName, status]) => {
          const fileId = Object.keys(uploadProgressMap).find(id => id.startsWith(fileName + '-'));
          const progress = fileId ? uploadProgressMap[fileId] : undefined;
          let line = fileName;
          if (progress !== undefined && status === 'Uploading...') {
            line += `: ${progress}%`;
          } else {
            line += `: ${status}`;
          }
          const color = status.startsWith('✅') ? 'green' : status.startsWith('❌') ? 'red' : undefined;
          const showLoader = currentUploading === fileName && status === 'Uploading...';
          return (
            <Group key={fileName} justify="space-between" align="center" style={{ width: '100%' }}>
              <Group gap="xs" align="center">
                {showLoader && (
                  <Loader size={14} />
                )}
                <Text size="sm" c={color}>
                  {line}
                </Text>
              </Group>
            </Group>
          );
        })}
        <Group justify="right" mt="md">
          <Button onClick={() => setShowProgressModal(false)} disabled={!!currentUploading}>
             Close
           </Button>
         </Group>
      </Modal>

      {/* Media browser for images/videos */}
      {getCurrentFolderId() && (
        <DriveMediaBrowser
          driveId={driveId}
          folderId={getCurrentFolderId()}
          opened={showMediaBrowser}
          startItemId={mediaStartId}
          onClose={() => setShowMediaBrowser(false)}
        />
      )}
    </Stack>
  );
};
